import {
  Account,
  cacheKeys,
  Issue as DomainIssue,
  FeatureName,
  getFullName,
  MediaType,
  Message,
  NewMessage,
  PermissionValues,
  PickerMode,
  useCreateIssueMessage,
  useCurrentAccount,
  useFindUnreadActivityInbox,
  useIsFeatureEnabled,
  useIssue,
  useActiveCallForIssue,
  useOnboardingIssueView,
  useSetActivityInboxRead,
  useSideChatSummaries,
  useStartSideChat,
  useStorageKeys,
  useUpdateIssue,
} from '@gripp/shared-logic';
import {
  type ImageGalleryImage,
  IssueContext,
  IssueMessagesRef,
  MessageThread,
  MessageThreadHeader,
  RoutineExecutionProps,
  RoutineModal,
  SlideAction,
  useSlideLayout,
} from '@gripp/shared-ui';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { activityPaths } from '../../activity/routes';
import { useAssetRouting } from '../../assets/assetRouteContext';
import {
  ALL_ACTIVITIES_QUERY_VALUE,
  assetPaths,
} from '../../assets/assetUtils';
import { trackAnonymousIssueId } from '../../anonymous/anonymousIssueIds';
import { useCall } from '../../calls/CallProvider';
import { MediaPicker } from '../../shared/components/media/mediaPicker';
import { getMediaBaseUrl } from '../../shared/mediaUtils';
import { useCurrentIssueContext } from '../issueContext';
import { computeBackRoute, computeLinkedIssueRoute } from './issueBackRouting';
import { IssueHeaderCropCircle } from './issueHeaderCropCircle';
import { ShareIssuePanel } from './shareIssuePanel';

type IssueProps = {
  issueId?: string;
  isComingFromActivities?: boolean;
  showAssetLink?: boolean;
  onBackPressGuard?: () => Promise<boolean>;
  /**
   * Supplied by callers that own a column stack (see `useRoutineImagePanel`).
   * Left out, routine media falls back to the modal lightbox.
   */
  onViewImages?: (images: ImageGalleryImage[], initialIndex: number) => void;
  /**
   * Share-link views (plan 0030 addendum). `readOnlyViewer` marks a signed-out
   * viewer: severity, Settings/print/overflow and the group-call poll are off
   * (each would FORBIDDEN as the Anonymous account); the composer stays on.
   * `anonymousSharedBy` is the link's creator — stamped as `guestOf` on
   * anonymous replies and shown in the "Anonymous (…'s Guest)" label until the
   * viewer registers and claims the conversation (`onAnonymousAuthorPress`).
   */
  readOnlyViewer?: boolean;
  anonymousSharedBy?: { id: string; name?: { first?: string; last?: string } };
  onAnonymousAuthorPress?: () => void;
  /** Show the asset link even in a left-only layout — see IssueHeaderProps. */
  forceShowAssetLink?: boolean;
  /** Replaces the slide-into-column asset navigation with a route change. */
  onAssetPressOverride?: (assetId: string) => void;
};

export const Issue = (props: IssueProps) => {
  return <IssueBase {...props} />;
};

export const IssueBase = (props: IssueProps): JSX.Element => {
  const { onSlideChange, prepareSlideTransition, setColumnTitle } =
    useSlideLayout();
  const { account: currentAccount } = useCurrentAccount();
  const call = useCall();
  const isCommunicationPlatformEnabled = useIsFeatureEnabled(
    FeatureName.CommunicationPlatform
  );
  // Per-message "Call" action, gated behind the Communication Platform feature
  // toggle and matched to the startCall resolver's call.create requirement (no
  // FORBIDDEN dead-end). `!!call` keeps the action hidden where CallProvider
  // isn't mounted at all — a signed-out share-link view — instead of offering
  // a menu row that silently does nothing.
  const canPlaceDirectCall =
    isCommunicationPlatformEnabled &&
    !!currentAccount?.hasPermission(PermissionValues.call.create) &&
    !!call;
  const [searchParams] = useSearchParams();
  const { setCurrentIssue } = useCurrentIssueContext() || {};
  const { issueId: routeIssueId, workspaceId } = useParams();
  const issueId = props.issueId ?? routeIssueId;

  // This Activity is handed to startCall so the backend logs the 1:1 call —
  // and its outcome — into this thread as a system line.
  const startDirectCall = useCallback(
    (actor: { id: string; name: string }) => call?.startCall(actor, issueId),
    [call, issueId]
  );
  const onCallActor = canPlaceDirectCall ? startDirectCall : undefined;

  // End-to-end calling demo (plan 0021 addendum): "Call in progress · Join"
  // pill on the first message, gated exactly like the per-message Call action.
  const canUseGroupCall =
    isCommunicationPlatformEnabled &&
    currentAccount?.hasPermission(PermissionValues.call.create);
  const { data: activeCall } = useActiveCallForIssue(issueId, {
    enabled: !!currentAccount,
  });
  const messagesRef = useRef<IssueMessagesRef>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { issue, isLoading } = useIssue(issueId ?? '');

  // Side Chat (plan 0022), gated exactly like the per-message Call action:
  // behind the Communication Platform feature toggle and matched to the
  // IssueMessage.create resolver requirement, so there's no FORBIDDEN
  // dead-end. When off, `onOpenSideChat` stays undefined (menu row + pill
  // hidden). Unlike Call, side chat is available on ANY message.
  const isSideChatEnabled =
    isCommunicationPlatformEnabled &&
    !!currentAccount?.hasPermission(PermissionValues.issuemessage.create);
  const { summaries: sideChatSummaries } = useSideChatSummaries(
    issueId ?? '',
    { enabled: isSideChatEnabled && !!issueId }
  );
  const { startSideChat } = useStartSideChat();

  const onOpenSideChat = isSideChatEnabled
    ? async (message: Message) => {
        const authorName = message.updatedBy?.name
          ? getFullName(message.updatedBy.name)
          : '';
        // Ensure the SYSTEM_MESSAGE divider exists (no-op if this message
        // already has a side chat), then open the panel as the rightmost column.
        // `issue` is loosely typed (useIssue → unknown); cast to the domain type
        // like the rest of this file (e.g. `issue as DomainIssue` below).
        const domainIssue = issue as DomainIssue;
        await startSideChat({
          issue: {
            id: domainIssue.id,
            workspace: { id: domainIssue.workspace.id },
          },
          originMessageId: message.id,
          authorName,
          summaries: sideChatSummaries,
        });
        prepareSlideTransition(SlideAction.NEXT);
        navigate(`/activities/${issueId}/sidechat/${message.id}`, {
          state: { slideLayoutInitialTransition: SlideAction.NEXT },
        });
      }
    : undefined;

  // Automatically advance onboarding when viewing an onboarding issue
  useOnboardingIssueView(issue);

  const [messagesCount, setMessagesCount] = useState<number | undefined>(
    undefined
  );
  const [showFooter, setShowFooter] = useState(true);
  const [shareVisible, setShareVisible] = useState(false);
  // Gated behind the Communication Platform flag; createShareLink enforces access.
  const canShare = isCommunicationPlatformEnabled;
  const { mutate: updateIssue } = useUpdateIssue();
  const { createIssueMessage } = useCreateIssueMessage();
  const storageKeys = useStorageKeys();
  const { t } = useTranslation();
  const [routineModalVisible, setRoutineModalVisible] = useState(false);
  const [routineModalProps, setRoutineModalProps] = useState<
    RoutineExecutionProps | undefined
  >();
  const queryClient = useQueryClient();
  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    onBeforePrint: async () => {
      setShowFooter(false);
      setIsPrinting(true);
      await new Promise((resolve) => setTimeout(resolve, 1));
    },
    onAfterPrint: () => {
      setShowFooter(true);
      setIsPrinting(false);
    },
    // pageStyle: '@page { margin: 10px; }',    // setting this causes images to not show up
    documentTitle: (issue as any)?.title,
  });
  const [isPrinting, setIsPrinting] = useState(false);
  const [issueKey, setIssueKey] = useState(0);

  const onPrint = async () => {
    handlePrint();
  };

  useEffect(() => {
    setMessagesCount(undefined);
  }, [issueId]);

  useEffect(() => {
    if (issue?.title) {
      setColumnTitle(issue.title);
    }
  }, [setColumnTitle, issue?.title]);

  useEffect(() => {
    return () => {
      setColumnTitle(undefined);
    };
  }, [setColumnTitle]);

  // Derive navigation target from the AssetRouteContext injected by the
  // nearest AssetRouteProvider.  Every render site wraps <Issue> with a
  // provider so listPath is always correct:
  //   assets context     → listPath '/assets',  basePath '/assets/:assetId'
  //   activities context → listPath '/activities'
  //   workspace context  → listPath '/workspaces'
  const { basePath: issueAssetBasePath, listPath: issueListPath } =
    useAssetRouting((issue as any)?.asset?.id ?? '');

  const onIssueDelete = () => {
    if ((issue as any)?.asset?.id && issueListPath === '/assets') {
      // Assets context: return to the specific asset home.
      navigate(issueAssetBasePath, { replace: true });
    } else {
      // Activities / workspace / no-asset context: return to the list root.
      navigate(issueListPath, { replace: true });
    }
  };

  const { data: inboxData } = useFindUnreadActivityInbox();

  const { setAsRead } = useSetActivityInboxRead();

  useEffect(() => {
    if (issue && inboxData) {
      setAsRead(issue.id, inboxData.items, true);
    }
  }, [issue, inboxData]);

  const viewAssetInfo = props.isComingFromActivities
    ? async () => {
        await onSlideChange(SlideAction.NEXT);
        navigate(`/assets/${issue.asset.id}/detail`);
      }
    : undefined;

  const onIssueHeaderChanged = (severity: string) => {
    if (props.readOnlyViewer) {
      return;
    }

    const input: any = {
      id: issueId,
      severity: severity,
    };

    // If Note (purple) changes to Report severity (red/yellow/green), convert to Report
    if (
      issue.type === 'NOTE' &&
      issue.severity === 'purple' &&
      ['red', 'yellow', 'green'].includes(severity)
    ) {
      input.type = 'REPORT';
    }

    updateIssue(
      { input: input },
      {
        onSettled: () => {
          const updated = {
            ...issue,
            ...input,
            updatedAt: new Date().toISOString(),
          };
          setCurrentIssue && setCurrentIssue(updated);
        },
      }
    );
  };

  const onNewMessage = async (message: NewMessage) => {
    const anonymousSharedBy = props.readOnlyViewer
      ? props.anonymousSharedBy
      : undefined;
    await createIssueMessage({
      issue: issue,
      message: message,
      anonymousGuestOf: anonymousSharedBy,
      options: {
        onSettled: async (data: Message) => {
          // The anonymous create response carries `guestOf { id }` only (the
          // Anonymous role cannot read the sharer's name), so the optimistic
          // row borrows the name from the resolved share target.
          const newMessage: Message =
            anonymousSharedBy && data?.guestOf
              ? {
                  ...data,
                  guestOf: { ...data.guestOf, ...anonymousSharedBy } as Account,
                }
              : data;
          messagesRef.current?.addNewMessage(newMessage);

          await queryClient.invalidateQueries({
            queryKey: cacheKeys('issue').all,
          });
        },
      },
    });

    if (anonymousSharedBy) {
      // Remembered so registration re-attributes this conversation (the same
      // mechanism the QR flow uses); and refetched because a signed-out viewer
      // has no message subscription to replace the optimistic row.
      await trackAnonymousIssueId(storageKeys.anonymousIssueIds, issue.id);
      await queryClient.invalidateQueries({
        queryKey: cacheKeys('issuemessage').all,
      });
    }
  };

  const onRoutineSelected = async (props: RoutineExecutionProps) => {
    setRoutineModalProps(props);
    setRoutineModalVisible(true);
  };

  const onRoutineCanceled = async () => {
    setRoutineModalVisible(false);

    if (routineModalProps?.onCanceled) {
      routineModalProps.onCanceled();
    }
  };

  const onRoutineCompleted = async () => {
    await queryClient.invalidateQueries({ queryKey: cacheKeys('issue').all });
    await queryClient.invalidateQueries({
      queryKey: cacheKeys('issuemessage').all,
    });
    await queryClient.invalidateQueries({
      queryKey: cacheKeys('workspaceasset').all,
    });
    setIssueKey((prev) => prev + 1);
    setRoutineModalVisible(false);

    if (routineModalProps?.onCompleted) {
      routineModalProps.onCompleted();
    }
  };

  const onRouteToIssue = async (issueId: string) => {
    navigate(
      computeLinkedIssueRoute(
        location.pathname,
        location.search,
        issueId,
        (issue as any)?.asset?.id,
        activityPaths.root,
        assetPaths.root
      )
    );
  };

  const onBackPress = props.isComingFromActivities
    ? async () => {
        if (props.onBackPressGuard) {
          const allowed = await props.onBackPressGuard();
          if (!allowed) return;
        }

        await BackRouting();
      }
    : async () => {
        await BackRouting();
      };

  const shouldUseConcurrentAssetBackTransition = () => {
    const segments = location.pathname.split('/').filter(Boolean);
    const isAssetActivity =
      segments[0] === assetPaths.root &&
      segments[2] === 'activity' &&
      segments.length >= 4;
    const isNestedActivityAssetActivity =
      segments[0] === activityPaths.root &&
      segments[2] === 'asset' &&
      segments[4] === 'activity' &&
      segments.length >= 6;

    return isAssetActivity || isNestedActivityAssetActivity;
  };

  const BackRouting = async () => {
    const decision = computeBackRoute(
      location.pathname,
      searchParams.get('from'),
      activityPaths.root,
      ALL_ACTIVITIES_QUERY_VALUE
    );

    if (decision.slide === 'SINGLE') {
      await onSlideChange(SlideAction.SINGLE);
      navigate(decision.url, { state: decision.state });
    } else if (shouldUseConcurrentAssetBackTransition()) {
      prepareSlideTransition(SlideAction.PREV);
      navigate(decision.url, {
        state: { slideLayoutInitialTransition: SlideAction.PREV },
      });
    } else {
      await onSlideChange(SlideAction.PREV);
      navigate(decision.url);
    }
  };

  const assetPressOverride = props.onAssetPressOverride;
  const onAssetPress = (issue as any)?.asset
    ? assetPressOverride
      ? () => assetPressOverride((issue as any).asset.id)
      : async () => {
        if (props.isComingFromActivities) {
          prepareSlideTransition(SlideAction.NEXT);
          navigate(`/activities/${issueId}/asset/${(issue as any).asset.id}`, {
            state: { slideLayoutInitialTransition: SlideAction.NEXT },
          });
        } else if (workspaceId) {
          navigate(`/workspaces/${workspaceId}`);
        } else {
          await onSlideChange(SlideAction.NEXT);
          navigate(`/assets/${(issue as any).asset.id}/activity/all`);
        }
      }
    : undefined;

  return (
    !isLoading &&
    issue && (
      <IssueContext.Provider
        value={{
          onRoutineSelected,
          onRouteToIssue,
          onViewImages: props.onViewImages,
        }}
      >
        <View style={[styles.width100, styles.flex1]} ref={printRef}>
          <MessageThread
            key={issueKey}
            ref={messagesRef}
            issue={issue as DomainIssue}
            onPostMessage={onNewMessage}
            mediaPicker={MediaPicker}
            mediaUrl={getMediaBaseUrl()}
            inbox={inboxData?.items ?? []}
            setAsRead={setAsRead}
            workspaceId={issue?.workspace?.id}
            pickerMode={PickerMode.ATTACHMENT}
            acceptedMediaTypes={[
              MediaType.Image,
              MediaType.Document,
              MediaType.Video,
            ]}
            showComposer={showFooter}
            onMessagesCountChange={setMessagesCount}
            onCall={onCallActor}
            onOpenSideChat={onOpenSideChat}
            sideChatSummaries={sideChatSummaries}
            onAnonymousAuthorPress={
              props.readOnlyViewer ? props.onAnonymousAuthorPress : undefined
            }
            callInProgress={canUseGroupCall && !!activeCall}
            onJoinCall={
              canUseGroupCall
                ? () =>
                    call?.joinActivityCall(
                      issueId ?? '',
                      issue?.title ?? 'Group call'
                    )
                : undefined
            }
            header={
              <MessageThreadHeader>
                <IssueHeaderCropCircle
                  issue={issue}
                  onChanged={onIssueHeaderChanged}
                  setShowFooter={setShowFooter}
                  titleStyle={styles.issueTitle}
                  viewAssetInfo={viewAssetInfo}
                  onIssueDelete={onIssueDelete}
                  onPrint={onPrint}
                  onBackPress={onBackPress}
                  onAssetPress={onAssetPress}
                  assetButtonLabel={
                    workspaceId ? t('workspace.titleSingular') : undefined
                  }
                  showAssetLink={props.showAssetLink}
                  forceShowAssetLink={props.forceShowAssetLink}
                  readOnly={props.readOnlyViewer}
                  onShare={
                    canShare && !props.readOnlyViewer
                      ? () => setShareVisible(true)
                      : undefined
                  }
                />
              </MessageThreadHeader>
            }
          />
        </View>
        <ShareIssuePanel
          issueId={(issue as DomainIssue).id}
          visible={shareVisible}
          onDismiss={() => setShareVisible(false)}
        />
        {routineModalProps && (
          <RoutineModal
            {...routineModalProps}
            visible={routineModalVisible}
            onCanceled={onRoutineCanceled}
            onCompleted={onRoutineCompleted}
          />
        )}
      </IssueContext.Provider>
    )
  );
};

const styles = StyleSheet.create({
  issueTitle: {
    paddingLeft: 15,
  },
  print: {
    flex: 1,
    margin: 10,
  },
  width100: {
    width: '100%',
  },
  flex1: {
    flex: 1,
    minHeight: 0,
  },
});
